import type { FC } from 'react';
import dp5 from './../../../resources/content/dp5.png';
import { TypesOfDPWrapper } from './TypesOfDP.styled';
import EmbedYoutube from '../../utils/EmbedYoutube/EmbedYoutube';

interface TypesOfDPProps {}

const TypesOfDP: FC<TypesOfDPProps> = () => (
 <TypesOfDPWrapper>
    <header className="entry-header">
      <h1 className="entry-title" itemProp="headline">Types of Dynamic Programming</h1>			
   </header>
   <br/>
   <br/>
   <div className="entry-content clear" itemProp="text"  style={{height: 'auto !important'}}>
      <p className="text-align-center">The five most common types of DP problems that every programmer should master!</p>
      
      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="435" src={dp5} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>

      <ul className="text-align-justify">
        <li className="linear"><strong>Linear Dynamic Programming</strong></li>
        <ul className="text-align-justify">
          <li>This is your go-to when you see problems talking about steps, climbing, or reaching a specific number or position.</li>
          <li>The key recognition pattern here is sequential progression ~ you're moving from one state to the next in a linear fashion. Each state depends on a fixed number of previous states.</li>
          <li>To identify, watch for keywords like 'steps,' 'ways,' and 'reach' in problem description.</li>
          <li>Classic examples include:</li>
          <ul className="text-align-justify">
            <li>Fibonacci Numbers</li>
	          <li>Climbing Stairs</li>
	          <li>House Robber</li>
	          <li>Jump Game</li>
          </ul>
        </ul>
      </ul>

      <ul className="text-align-justify">
        <li className="kadane"><strong>Kadane's Algorithm</strong></li>
        <ul className="text-align-justify">
          <li>If you see 'maximum sum,' 'length of subarray,' or 'contiguous values,' this is likely your answer.</li>
          <li>The approach involves finding maximum or minimum values within subarrays. You're essentially sliding through the array, keeping track of the best contiguous sequence.</li>
          <li>To identify, look for keywords like 'max sum' and 'contiguous' in problem description.</li>
          <li>Classic examples include:</li>
          <ul className="text-align-justify">
            <li>Maximum Subarray</li>
	          <li>Maximum Circular Subarray Sum</li>
	          <li>Maximum Product Subarray</li>
	          <li>Best Time to Buy and Sell Stock</li>
          </ul>
        </ul>
      </ul>

      <ul className="text-align-justify">
        <li className="knapsack"><strong>0/1 Knapsack</strong></li>
        <ul className="text-align-justify">
          <li>You'll see two key characteristics here:</li>
          <ul className="text-align-justify">
	          <li>You have a list of items or choices. </li>
	          <li>You face capacity or budget limits ~ these constraints are **limited**, meaning each item can only be used once.</li>
          </ul>
          <li>Your approach involves deciding whether to pick or skip each item while respecting constraints like weight, time, or budget.</li>
          <li>To identify, look for keywords like 'weights,' 'capacity,' and 'value' in problem statements.</li>
          <li>Classic examples include:</li>
          <ul className="text-align-justify">
            <li>Classic 0/1 Knapsack</li>
	          <li>Subset Sum</li>
	          <li>Partition Equal Subset Sum</li>
	          <li>Target Sum</li>
          </ul>
        </ul>
      </ul>

      <ul className="text-align-justify">
        <li className="unbounded"><strong>Unbounded Knapsack</strong></li>
        <ul className="text-align-justify">
          <li>It is similar to 0/1 Knapsack, but with one crucial difference.</li>
          <li>While you still have a list of choices and items, here you have **no capacity or budget limits** ~ or rather, **unlimited** usage of each item. This means you can use any item multiple times, giving you infinite supply.</li>
          <li>Look for same keywords as 0/1: 'weights,' 'capacity,' 'value'~ but the unlimited aspect is the key differentiator.</li>
          <li>Classic examples include:</li>
          <ul className="text-align-justify">
            <li>Classic Unbounded Knapsack</li>
            <li>Coin Change</li>
            <li>Coin Change II</li>
            <li>Rod Cutting</li>
          </ul>
        </ul>
      </ul>

      <ul className="text-align-justify">
        <li className="subsequence"><strong>Subsequence</strong></li>
        <ul className="text-align-justify">
          <li>These problems involve matching, transforming, or comparing strings and sequences. You're looking for patterns, similarities, or optimal transformations.</li>
          <li>The approach typically involves comparing or transforming strings and sequences, often using 2D DP tables to track relationships between characters or elements.</li>
          <li>Watch for 'similarity' and 'subsequence' in problem descriptions.</li>
          <li>Classic examples include:</li>
          <ul className="text-align-justify">
            <li>Longest Common Subsequence</li>
            <li>Edit Distance</li>
            <li>Longest Palindromic Subsequence</li>
            <li>Distinct Subsequences</li>
          </ul>
        </ul>
      </ul>

      <br />
      <EmbedYoutube videoType="video" videoList={["Xp32RagPLgk"]} />

   </div>
 </TypesOfDPWrapper>
);

export default TypesOfDP;
